import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles,
  Chip,
} from "@material-ui/core";
import { Add, DeleteOutline, Edit } from "@material-ui/icons";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

const emptyForm = () => ({
  id: "",
  name: "",
  message_pattern: "",
  match_mode: "contains",
  pixel_id: "",
  campaign_id: "",
  adset_id: "",
  ad_id: "",
  utm_source: "whatsapp",
  utm_medium: "cpc",
  utm_campaign: "",
  utm_content: "",
  utm_term: "",
  currency: "EUR",
  enabled: true,
});

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  hint: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  patternCell: {
    maxWidth: 280,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(1.5),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  fullWidth: {
    gridColumn: "1 / -1",
  },
}));

const Campanhas = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [trackings, setTrackings] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [deleting, setDeleting] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadTrackings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/trackings");
      setTrackings(Array.isArray(data.trackings) ? data.trackings : []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTrackings();
  }, [loadTrackings]);

  const openCreate = () => {
    setForm(emptyForm());
    setModalOpen(true);
  };

  const openEdit = (tracking) => {
    setForm({
      ...emptyForm(),
      ...tracking,
      enabled: tracking.enabled !== false,
    });
    setModalOpen(true);
  };

  const handleChange = (field) => (event) => {
    const value =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.message_pattern.trim()) {
      toast.error(i18n.t("campanhasPage.toasts.required"));
      return;
    }
    if (!String(form.pixel_id || "").replace(/\D/g, "")) {
      toast.error(i18n.t("campanhasPage.toasts.pixelRequired"));
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.id) delete payload.id;
      delete payload.stripe_link;
      delete payload.default_value;
      await api.post("/trackings", payload);
      toast.success(i18n.t("campanhasPage.toasts.saved"));
      setModalOpen(false);
      await loadTrackings();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/trackings/${id}`);
      toast.success(i18n.t("campanhasPage.toasts.deleted"));
      await loadTrackings();
    } catch (err) {
      toastError(err);
    } finally {
      setDeleting(null);
      setConfirmOpen(false);
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deleting
            ? `${i18n.t("campanhasPage.confirmation.deleteTitle")} ${deleting.name}?`
            : ""
        }
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={() => handleDelete(deleting.id)}
      >
        {i18n.t("campanhasPage.confirmation.deleteMessage")}
      </ConfirmationModal>

      <MainHeader>
        <Title>{i18n.t("campanhasPage.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={openCreate}
          >
            {i18n.t("campanhasPage.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Typography className={classes.hint}>
          {i18n.t("campanhasPage.hint")}
        </Typography>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{i18n.t("campanhasPage.table.name")}</TableCell>
              <TableCell>{i18n.t("campanhasPage.table.pattern")}</TableCell>
              <TableCell>{i18n.t("campanhasPage.table.mode")}</TableCell>
              <TableCell>{i18n.t("campanhasPage.table.metaIds")}</TableCell>
              <TableCell>{i18n.t("campanhasPage.table.status")}</TableCell>
              <TableCell align="center">
                {i18n.t("campanhasPage.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRowSkeleton columns={6} />
            ) : trackings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  {i18n.t("campanhasPage.empty")}
                </TableCell>
              </TableRow>
            ) : (
              trackings.map((trk) => (
                <TableRow key={trk.id}>
                  <TableCell>{trk.name}</TableCell>
                  <TableCell
                    className={classes.patternCell}
                    title={trk.message_pattern}
                  >
                    {trk.message_pattern}
                  </TableCell>
                  <TableCell>
                    {trk.match_mode === "exact"
                      ? i18n.t("campanhasPage.modes.exact")
                      : i18n.t("campanhasPage.modes.contains")}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" display="block">
                      PX: {trk.pixel_id || "—"}
                    </Typography>
                    <Typography variant="caption" display="block">
                      C: {trk.campaign_id || "—"}
                    </Typography>
                    <Typography variant="caption" display="block">
                      AS: {trk.adset_id || "—"}
                    </Typography>
                    <Typography variant="caption" display="block">
                      AD: {trk.ad_id || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={
                        trk.enabled === false
                          ? i18n.t("campanhasPage.status.off")
                          : i18n.t("campanhasPage.status.on")
                      }
                      color={trk.enabled === false ? "default" : "primary"}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => openEdit(trk)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setDeleting(trk);
                        setConfirmOpen(true);
                      }}
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog
        open={modalOpen}
        onClose={() => !saving && setModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {form.id
            ? i18n.t("campanhasPage.modal.edit")
            : i18n.t("campanhasPage.modal.create")}
        </DialogTitle>
        <DialogContent dividers>
          <div className={classes.formGrid}>
            <TextField
              className={classes.fullWidth}
              label={i18n.t("campanhasPage.form.name")}
              value={form.name}
              onChange={handleChange("name")}
              variant="outlined"
              size="small"
              required
            />
            <TextField
              className={classes.fullWidth}
              label={i18n.t("campanhasPage.form.messagePattern")}
              value={form.message_pattern}
              onChange={handleChange("message_pattern")}
              variant="outlined"
              size="small"
              required
              helperText={i18n.t("campanhasPage.form.messagePatternHelp")}
            />
            <FormControl variant="outlined" size="small">
              <InputLabel>{i18n.t("campanhasPage.form.matchMode")}</InputLabel>
              <Select
                value={form.match_mode}
                onChange={handleChange("match_mode")}
                label={i18n.t("campanhasPage.form.matchMode")}
              >
                <MenuItem value="contains">
                  {i18n.t("campanhasPage.modes.contains")}
                </MenuItem>
                <MenuItem value="exact">
                  {i18n.t("campanhasPage.modes.exact")}
                </MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={form.enabled}
                  onChange={handleChange("enabled")}
                  color="primary"
                />
              }
              label={i18n.t("campanhasPage.form.enabled")}
            />
            <TextField
              className={classes.fullWidth}
              label={i18n.t("campanhasPage.form.pixelId")}
              value={form.pixel_id}
              onChange={handleChange("pixel_id")}
              variant="outlined"
              size="small"
              required
              helperText={i18n.t("campanhasPage.form.pixelIdHelp")}
            />
            <TextField
              label={i18n.t("campanhasPage.form.campaignId")}
              value={form.campaign_id}
              onChange={handleChange("campaign_id")}
              variant="outlined"
              size="small"
            />
            <TextField
              label={i18n.t("campanhasPage.form.adsetId")}
              value={form.adset_id}
              onChange={handleChange("adset_id")}
              variant="outlined"
              size="small"
            />
            <TextField
              label={i18n.t("campanhasPage.form.adId")}
              value={form.ad_id}
              onChange={handleChange("ad_id")}
              variant="outlined"
              size="small"
            />
            <TextField
              label={i18n.t("campanhasPage.form.currency")}
              value={form.currency}
              onChange={handleChange("currency")}
              variant="outlined"
              size="small"
              helperText={i18n.t("campanhasPage.form.currencyHelp")}
            />
            <TextField
              label="utm_source"
              value={form.utm_source}
              onChange={handleChange("utm_source")}
              variant="outlined"
              size="small"
            />
            <TextField
              label="utm_medium"
              value={form.utm_medium}
              onChange={handleChange("utm_medium")}
              variant="outlined"
              size="small"
            />
            <TextField
              label="utm_campaign"
              value={form.utm_campaign}
              onChange={handleChange("utm_campaign")}
              variant="outlined"
              size="small"
            />
            <TextField
              label="utm_content"
              value={form.utm_content}
              onChange={handleChange("utm_content")}
              variant="outlined"
              size="small"
            />
            <TextField
              label="utm_term"
              value={form.utm_term}
              onChange={handleChange("utm_term")}
              variant="outlined"
              size="small"
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)} disabled={saving}>
            {i18n.t("campanhasPage.buttons.cancel")}
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleSave}
            disabled={saving}
          >
            {i18n.t("campanhasPage.buttons.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default Campanhas;
